import { useEffect, useRef } from 'react';

const SESSION_TIMEOUT = 28 * 60 * 1000; // 28 minutes in milliseconds

const useIdleSessionHandler = (onSessionTimeout: () => void) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const activeTimeRef = useRef<number>(-1);

  // Reset session timer
  const startSession = () => {
    stopSession();
    timerRef.current = setTimeout(() => {
      onSessionTimeout(); // Trigger session timeout alert
      stopSession(); // Stop the session timer
    }, SESSION_TIMEOUT);
    activeTimeRef.current = Date.now();
  };

  // Stop session timer
  const stopSession = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    startSession(); // Auto start session when hook is mounted
    return stopSession; // Cleanup on unmount
  }, []);

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      const idletime = Date.now() - activeTimeRef.current;
      if (activeTimeRef.current !== -1 && idletime > SESSION_TIMEOUT) {
        onSessionTimeout();
        stopSession();
      }
    }
  };

  useEffect(() => {
    window.document.addEventListener('visibilitychange', handleVisibilityChange); // Auto start session when hook is mounted
    return () => {
      window.document.removeEventListener('visibilitychange', handleVisibilityChange);
    }; // Cleanup on unmount
  }, []);

  return { resetSession: startSession };
};

export default useIdleSessionHandler;
